import { useEffect } from 'react';

/**
 * Sets document.title and the og:/twitter: meta tags for the current page,
 * restoring the previous values on unmount. This is the v1 (client-side) SEO
 * story — correct in-browser and for JS-rendering crawlers. Static-HTML
 * crawlers (FB/X/LinkedIn previews) are handled in phase 2 (server-injected
 * tags); see the design spec §7.
 */

type MetaSelector =
  | { attr: 'property'; value: string }
  | { attr: 'name'; value: string };

const META_TAGS: { key: string; selector: MetaSelector }[] = [
  // Standard meta description — what Google shows in the result snippet.
  { key: 'description', selector: { attr: 'name', value: 'description' } },
  { key: 'title', selector: { attr: 'property', value: 'og:title' } },
  {
    key: 'description',
    selector: { attr: 'property', value: 'og:description' },
  },
  { key: 'image', selector: { attr: 'property', value: 'og:image' } },
  { key: 'title', selector: { attr: 'name', value: 'twitter:title' } },
  {
    key: 'description',
    selector: { attr: 'name', value: 'twitter:description' },
  },
  { key: 'image', selector: { attr: 'name', value: 'twitter:image' } },
  { key: 'card', selector: { attr: 'name', value: 'twitter:card' } },
];

function findMeta(selector: MetaSelector): HTMLMetaElement | null {
  return document.head.querySelector<HTMLMetaElement>(
    `meta[${selector.attr}="${selector.value}"]`
  );
}

function upsertMeta(
  selector: MetaSelector,
  content: string
): { element: HTMLMetaElement; created: boolean; previous: string | null } {
  let element = findMeta(selector);
  let created = false;
  let previous: string | null = null;

  if (element) {
    previous = element.getAttribute('content');
  } else {
    element = document.createElement('meta');
    element.setAttribute(selector.attr, selector.value);
    document.head.appendChild(element);
    created = true;
  }

  element.setAttribute('content', content);
  return { element, created, previous };
}

export default function useDocumentMeta(
  title: string,
  description?: string,
  ogImage?: string
): void {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    const values: Record<string, string | undefined> = {
      title,
      description,
      image: ogImage,
      card: ogImage ? 'summary_large_image' : 'summary',
    };

    const restorers: Array<() => void> = [];

    META_TAGS.forEach(({ key, selector }) => {
      const content = values[key];
      if (content === undefined || content === '') return;

      const { element, created, previous } = upsertMeta(selector, content);

      restorers.push(() => {
        if (created) {
          element.remove();
        } else if (previous !== null) {
          element.setAttribute('content', previous);
        } else {
          element.removeAttribute('content');
        }
      });
    });

    return () => {
      document.title = previousTitle;
      restorers.forEach((restore) => restore());
    };
  }, [title, description, ogImage]);
}
